/* ---------------------------------------------------------------------------
 * Shared helpers + small presentational components used by both the main App
 * bundle and the lazy-loaded chart chunks (ServerModal, Insights,
 * PopulationChart). Keep this module light: nothing here may import recharts
 * or any other heavy dependency, or the code-splitting win evaporates.
 * ------------------------------------------------------------------------- */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button, Modal } from '@heroui/react'
import { Info, StarFilled } from './Icons'
import { Star as StarOutline } from 'lucide-react'

/* Chart/timeseries range keys shared by the site's API layer and every chart. */
export type Range = '1d' | '7d' | '2w' | '1m' | 'all'

/* ----------------------------------------------------------------- Tooltip
 * One snappy, branded tooltip for the whole site. Replaces the native `title=`
 * attribute (which has a ~500ms delay and renders as an ugly OS bubble).
 *  - shows instantly on hover/focus, hides on leave/blur
 *  - `tappable` (for info icons / tags, not action buttons) toggles on tap so
 *    touch users can read it; it stops propagation so it never triggers the host
 *  - portal to <body> + position:fixed so it escapes the modal's overflow clip,
 *    clamped to the viewport, flips below the trigger when there's no room above
 * Content is a ReactNode, so callers can pass a rich title + body block. */
export function TipTitle({ children }: { children: ReactNode }) {
  return <div className="mono text-[11px] uppercase tracking-wider text-[var(--foreground)] mb-1.5">{children}</div>
}
export function Tip({ content, children, width = 256, tappable = false, className = '' }: {
  content: ReactNode
  children: ReactNode
  width?: number
  tappable?: boolean
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ left: number; top: number; w: number; below: boolean } | null>(null)
  const place = () => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 10
    const w = Math.min(width, window.innerWidth - margin * 2)
    let left = r.left + r.width / 2 - w / 2
    left = Math.max(margin, Math.min(left, window.innerWidth - margin - w))
    const below = r.top < 180   // not enough room above → drop below the trigger
    setPos({ left, top: below ? r.bottom + 8 : r.top - 8, w, below })
  }
  useLayoutEffect(() => { if (open) place() }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return
    const reflow = () => place()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', reflow, true)
    window.addEventListener('resize', reflow)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', reflow, true)
      window.removeEventListener('resize', reflow)
      window.removeEventListener('keydown', onKey)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span ref={ref} className={`inline-flex ${className}`} tabIndex={0}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      onClick={tappable ? (e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o) } : undefined}>
      {children}
      {open && pos != null && createPortal(
        <div role="tooltip"
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.w,
                   transform: pos.below ? undefined : 'translateY(-100%)' }}
          className="z-[120] pointer-events-none rounded-[12px] bg-[#0e0f11] ring-1 ring-white/10 shadow-[0_8px_30px_rgba(0,0,0,.6)] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-[var(--muted)] animate-[tipin_90ms_ease-out]">
          {content}
        </div>,
        document.body)}
    </span>
  )
}

export const RANGES: Range[] = ['1d', '7d', '2w', '1m', 'all']

// Chart palette. `primary` is the brand orange (same hex as --brand in
// index.css) so the main data line on every chart matches the logo accent.
// `secondary` is the muted gray used for comparison series (e.g. ping in the
// server detail chart). The `dots` color is sampled in the SVG `<pattern>`
// fills used in place of solid translucent area fills.
export const C = { primary: '#FF3C15', secondary: '#7b8290', grid: '#23262b', axis: '#6b7280', dots: '#FF3C15' }

export type Metric = 'players' | 'servers'
export const METRIC_LABEL: Record<Metric, string> = { players: 'Players online', servers: 'Servers online' }

/* ----------------------------------------------------------- Brand recipes
 * THE one filter-chip style (see docs/STYLE.md — the locked brand guide).
 * Active = brand-orange text + orange ring + soft orange fill, NEVER the
 * near-white --accent token. Import these instead of hand-rolling classes;
 * if a chip somewhere doesn't use them, that's a bug. */
export const CHIP_ON =
  'bg-[var(--brand-soft)] text-[var(--brand)] font-semibold ring-1 ring-[var(--brand)] [--icon-accent:var(--brand)]'
export const CHIP_OFF =
  'bg-[var(--background-tertiary)] text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]'
export const chipClass = (on: boolean) =>
  `mono text-xs rounded-full transition-colors ${on ? CHIP_ON : CHIP_OFF}`

// Centered moving-average "trend" over a value series — smooths jagged raw
// counts into an overall direction. Preserves array length (overlays the raw
// series 1:1) and nulls (gaps stay gaps). Window defaults to ~1/8 of the series.
export function smoothTrend(vals: (number | null)[], window?: number): (number | null)[] {
  const n = vals.length
  const half = Math.floor(Math.max(2, window ?? Math.round(n / 8)) / 2)
  return vals.map((v, i) => {
    if (v == null) return null
    let sum = 0, cnt = 0
    for (let j = Math.max(0, i - half); j <= Math.min(n - 1, i + half); j++) {
      const x = vals[j]
      if (x != null) { sum += x; cnt++ }
    }
    return cnt ? sum / cnt : null
  })
}

export function labelFor(t: string, r: Range, tz?: string) {
  const d = new Date(t)
  const z = tz ? { timeZone: tz } : {}
  return r === '1d' || r === '7d'
    ? d.toLocaleString([], { ...z, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { ...z, month: 'short', day: 'numeric' })
}

// Minutes east of UTC for a timezone right now (DST-aware).
export function tzOffsetMin(tz: string): number {
  try {
    const now = new Date()
    const u = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const l = new Date(now.toLocaleString('en-US', { timeZone: tz }))
    return Math.round((l.getTime() - u.getTime()) / 60000)
  } catch { return 0 }
}
// Short label like "GMT+2" / "PDT" for the current time in a tz.
export function tzLabel(tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || tz
  } catch { return tz }
}

export const axisNum = (n: number) =>
  n >= 1_000_000 ? (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  : n >= 1000 ? Math.round(n / 1000) + 'k' : String(n)
// time_accel is stored as a float32, so 0.1 comes back as 0.10000000149… —
// round to 2dp for display and strip trailing zeros.
export const accel = (n: number | null | undefined) => n == null ? '?' : String(Math.round(n * 100) / 100)

export const latencyWord = (ms: number) => ms <= 60 ? 'Low' : ms <= 120 ? 'Medium' : ms <= 200 ? 'High' : 'Very high'

// Star: filled state uses the custom StarFilled (brand-matched chunky glyph),
// unfilled state uses lucide's outline Star. Two rules:
//   1. 16px floor — both glyphs render at >= 16px even when the callsite
//      asks for 12 or 14, so the favourite control is never tiny.
//   2. Outline renders 1px larger with a 2px stroke so it doesn't look
//      visually thinner than the filled glyph sitting next to it.
//   3. When filled (active = favourited), the glyph is brand-orange.
//      StarFilled paints with fill="currentColor", so `color: var(--brand)`
//      tints the whole star without touching --icon-accent.
export function Star({ size, className, fill }: { size?: number; className?: string; fill?: string }) {
  const active = fill === 'currentColor'
  const s = Math.max(16, size ?? 16)
  return active
    ? <StarFilled size={s} noFloor className={className} style={{ color: 'var(--brand)' }} />
    : <StarOutline size={s} strokeWidth={2} className={className} />
}

/* ------------------------------------------------------------------ Range tabs */
export function RangeTabs({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex rounded-lg bg-[var(--background-tertiary)] p-0.5 gap-0.5">
      {RANGES.map((r) => (
        <button key={r} type="button" onClick={() => onChange(r)} aria-pressed={value === r}
          className={`mono text-xs px-2.5 py-1 rounded-md transition-colors ${value === r
            ? 'bg-[var(--accent)] text-[var(--accent-foreground)] font-medium'
            : 'text-[var(--muted)] hover:text-[var(--foreground)]'}`}>
          {r}
        </button>
      ))}
    </div>
  )
}

// Vanilla Scale band indicator — 6 filled segments (one per band) lighting up to
// the server's band, brand-orange. Measures installed mod systems (see tooltip).
const VS_SEGMENTS = 6
export function VanillaBar({ score, band, showLabel }: { score: number | null; band: string | null; showLabel?: boolean }) {
  if (score == null || band == null) return <span className="text-[var(--muted)]">—</span>
  // Fill segments by the actual 0–100 score, not the band index, so the bar matches
  // the number (e.g. 49/100 ≈ half). Any non-zero score lights at least one segment.
  const filled = score <= 0 ? 0 : Math.max(1, Math.round((score / 100) * VS_SEGMENTS))
  return (
    <span className="group inline-flex items-center gap-1.5 align-middle"
      title={`Mod level ${Math.round(score)}/100 · ${band} — graded from installed mods, not loot/economy tuning.`}>
      {!showLabel && (
        <span className="mono text-[11px] tabular-nums text-[var(--foreground)] w-6 text-right opacity-0 group-hover:opacity-100 transition-opacity">{Math.round(score)}</span>
      )}
      <span className="inline-flex gap-[2px]">
        {Array.from({ length: VS_SEGMENTS }, (_, i) => (
          <span key={i} className="w-[5px] h-[10px] rounded-[1px]"
            style={{ background: i < filled ? '#FF3C15' : 'var(--background-tertiary)' }} />
        ))}
      </span>
      {showLabel && <span className="mono text-xs text-[var(--foreground)]">{band} · {Math.round(score)}/100</span>}
    </span>
  )
}

// Inline "How we rank" trigger + explainer modal. We're deliberately vague about
// the formula (it's proprietary) but plain about the principles, so players know
// the Rank is honest and hard to game without us handing fake-farmers a recipe.
// THE one info-affordance glyph — every "ⓘ" across the site uses this so they
// are all the same 16px, same muted→foreground hover. Don't hand-roll <Info>.
export function InfoGlyph({ className = '' }: { className?: string }) {
  return <Info size={18} noFloor className={`text-[var(--muted)] hover:text-[var(--foreground)] cursor-help shrink-0 transition-colors ${className}`} />
}
export function RankInfo({ label = '' }: { label?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        aria-label="How we rank servers"
        className="inline-flex items-center gap-1.5 align-middle normal-case tracking-normal text-[var(--muted)] hover:text-[var(--foreground)] transition-colors">
        <InfoGlyph className="hover:text-inherit" />
        {label && <span className="text-xs leading-none">{label}</span>}
      </button>
      {open && (
        <Modal.Backdrop isOpen onOpenChange={(o) => !o && setOpen(false)}>
          <Modal.Container>
            <Modal.Dialog className="max-w-[460px] w-full">
              <Modal.Header><Modal.Heading className="text-lg">How we rank servers</Modal.Heading></Modal.Header>
              <Modal.Body className="space-y-3 text-sm text-[var(--muted)] leading-relaxed">
                <p>
                  Every server gets a <span className="text-[var(--foreground)]">Rank</span> from our
                  Quality Score model. The model rewards genuine, sustained
                  population, solid uptime, a lively in-game crowd, and real
                  demand.
                </p>
                <p>
                  It uses statistical smoothing so a brand-new server with a few lucky readings
                  cannot fluke the top of the board, and fake / booster / clone-farm servers are
                  excluded entirely before the scoring runs.
                </p>
                <p>
                  The exact formula is proprietary. The principle is simple:
                  <span className="text-[var(--foreground)]"> #1 is the best of the best by measures that are hard to fake.</span>
                </p>
                <p className="mono text-[11px] text-[var(--muted)] pt-1">
                  Ranks update periodically and a server needs a few hours of online history before
                  it earns one. Unranked servers show as <span className="text-[var(--foreground)]">—</span>.
                </p>
              </Modal.Body>
              <Modal.Footer><Button slot="close" variant="secondary">Got it</Button></Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      )}
    </>
  )
}

export function Sparkline({ data, color = '#FF3C15', height = 28 }: { data: number[]; color?: string; height?: number }) {
  // Pattern id has to be unique per instance so multiple sparklines on the same
  // page don't share + clobber each other's defs (React renders many cards).
  // useId is stable across renders; strip its punctuation (":", "«») since the
  // id is referenced through a CSS url(#…) which chokes on those characters.
  const patternId = 'sparkDots-' + useId().replace(/[^a-zA-Z0-9_-]/g, '')
  if (data.length < 2) return null
  const w = 120, h = height
  const mn = Math.min(...data), mx = Math.max(...data)
  const span = Math.max(1, mx - mn)
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - mn) / span) * (h - 2) - 1}`).join(' ')
  const area = `0,${h} ${pts} ${w},${h}`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width={w} height={h} aria-hidden style={{ overflow: 'visible' }}>
      <defs>
        <pattern id={patternId} width="5" height="5" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.7" fill={color} opacity="0.45" />
        </pattern>
      </defs>
      <polygon points={area} fill={`url(#${patternId})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.4} strokeLinejoin="round" />
    </svg>
  )
}

/* Stat card: label row (with optional info affordance) + a big mono value.
   The details modal's tile, shared so the launcher's join sheet shows the
   same pre-game figures in the same style. */
export function StatCard({ label, info, children, className = '' }: {
  label: string; info?: ReactNode; children: ReactNode; className?: string
}) {
  return (
    <div className={`relative rounded-[18px] bg-[#242427] p-4 min-h-[92px] flex flex-col justify-between ${className}`}>
      <div className="mono text-[11px] uppercase tracking-wider text-[var(--muted)] flex items-center gap-2.5"><span>{label}</span>{info}</div>
      {children}
    </div>
  )
}

/* Progress bar: a filled brand track on the recessed well, nothing else
   (STYLE.md: filled bars only). The launcher's join sheet and the server
   modal's Connect card share it for Workshop download progress. */
export function ProgressBar({ done, total, h = 6, className = '' }: { done: number; total: number; h?: number; className?: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  return (
    <span className={`block rounded-full bg-[#141517] overflow-hidden ${className}`} style={{ height: h }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <span className="block h-full rounded-full bg-[var(--brand)] transition-[width] duration-500" style={{ width: `${pct}%` }} />
    </span>
  )
}

/* Feature gates the apps flip at startup. The launcher keeps everything on
   (it is the owner's test build); the site turns off what is not public yet.
   Mutate before the first render: `features.wipes = false`. */
export const features = { wipes: true }
